import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Card, Row, Col, Tooltip, Alert, Divider } from 'antd';
import ReactECharts from 'echarts-for-react';

// 样式定义
const styles = {
  analysisItem: {
    textAlign: 'center' as const,
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '4px',
  },
  analysisTitle: {
    color: '#666',
    marginBottom: '8px',
  },
  analysisValue: {
    fontSize: '20px',
    fontWeight: 'bold' as const,
    color: '#1890ff',
  }
};

// 定义pH电荷数据点类型
interface ChargeDataPoint {
  ph: number;
  charge: number;
  sequence_length?: number | string;
}

// pH电荷曲线结果展示组件
const ChargeProfileResult: React.FC<ResultComponentProps> = ({ 
  data, 
  title, 
  onDeleteResult, 
  multiResults 
}) => {
  const normalizedResults = React.useMemo(() => {
    if (data && data.results && Array.isArray(data.results)) {
      if (data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)) {
        return data.results[0].result.results;
      }
      return data.results;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [] as any[];
  }, [data]);

  // 支持多序列显示
  if (normalizedResults.length > 1) {
    const successResults = normalizedResults.filter((item: any) => (item.success !== false) && (item.result?.charge_profile || item.charge_profile));
    
    if (successResults.length === 0) {
      return (
        <BaseResultDisplay
          title={title || 'pH电荷曲线'}
          data={data}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          showRawData={true}
          showDistribution={false}
        >
          <Alert
            message="无有效数据"
            description="没有找到有效的pH电荷曲线分析结果。"
            type="warning"
            showIcon
          />
        </BaseResultDisplay>
      );
    }

    return (
      <BaseResultDisplay
        title={title || `pH电荷曲线（${successResults.length}个序列）`}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
      >
        {successResults.map((item: any, index: number) => {
          const result = item.result || item;
          const chargeData: ChargeDataPoint[] = [];
          
          if (Array.isArray(result.charge_profile)) {
            result.charge_profile.forEach((chargeItem: any) => {
              if (typeof chargeItem === 'object' && chargeItem !== null && 'ph' in chargeItem && 'charge' in chargeItem) {
                chargeData.push({
                  ph: Number(chargeItem.ph),
                  charge: Number(chargeItem.charge),
                  sequence_length: result.sequence_length
                });
              }
            });
          }

          if (chargeData.length === 0) return null;

          chargeData.sort((a, b) => a.ph - b.ph);
          const chartData = chargeData.map(point => [point.ph, point.charge]);

          const findIsoelectricPoint = () => {
            for (let i = 0; i < chargeData.length - 1; i++) {
              if ((chargeData[i].charge >= 0 && chargeData[i+1].charge <= 0) ||
                  (chargeData[i].charge <= 0 && chargeData[i+1].charge >= 0)) {
                const ph1 = chargeData[i].ph;
                const ph2 = chargeData[i+1].ph;
                const charge1 = chargeData[i].charge;
                const charge2 = chargeData[i+1].charge;
                if (charge1 === charge2) return ph1;
                const isoelectricPoint = ph1 + (0 - charge1) * (ph2 - ph1) / (charge2 - charge1);
                return isoelectricPoint.toFixed(2);
              }
            }
            return "未找到";
          };

          const maxCharge = Math.max(...chargeData.map(point => point.charge));
          const minCharge = Math.min(...chargeData.map(point => point.charge));
          const sequenceLength = chargeData[0]?.sequence_length || '未知';

          const option = {
            tooltip: {
              trigger: 'axis',
              formatter: function(params: any) {
                const dataPoint = params[0];
                return `pH: ${dataPoint.data[0].toFixed(2)}<br/>电荷: ${dataPoint.data[1].toFixed(2)}`;
              }
            },
            grid: {
              left: '5%',
              right: '5%',
              bottom: '10%',
              top: '10%',
              containLabel: true
            },
            xAxis: {
              type: 'value',
              name: 'pH值',
              nameLocation: 'middle',
              nameGap: 30,
              min: Math.floor(Math.min(...chargeData.map(point => point.ph))),
              max: Math.ceil(Math.max(...chargeData.map(point => point.ph)))
            },
            yAxis: {
              type: 'value',
              name: '电荷',
              nameLocation: 'middle',
              nameGap: 40
            },
            series: [
              {
                type: 'line',
                data: chartData,
                smooth: true,
                lineStyle: {
                  width: 2,
                  color: '#52c41a'
                },
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: {
                  color: '#52c41a'
                }
              }
            ]
          };

          return (
            <div key={index}>
              {index > 0 && <Divider />}
              <Card 
                title={`序列 ${item.sequence_id || item.id || (index + 1)} - pH电荷曲线`}
                style={{ marginBottom: 16 }}
              >
                <ReactECharts
                  option={option}
                  style={{ height: '400px', width: '100%' }}
                  notMerge={true}
                />
                <Card title="电荷分析" bordered={false} style={{ marginTop: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>等电点 (pI)</div>
                        <div style={styles.analysisValue}>{findIsoelectricPoint()}</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>最大电荷</div>
                        <div style={styles.analysisValue}>{maxCharge.toFixed(2)}</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>最小电荷</div>
                        <div style={styles.analysisValue}>{minCharge.toFixed(2)}</div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Card>
            </div>
          );
        })}
      </BaseResultDisplay>
    );
  }

  // 单序列结果直接渲染（复用多序列的渲染逻辑，避免递归调用自身导致无限循环）
  const singleEntry = normalizedResults.length === 1 ? normalizedResults[0] : null;

  if (singleEntry && (singleEntry.result?.charge_profile || singleEntry.charge_profile)) {
    const result = singleEntry.result || singleEntry;
    const chargeData: ChargeDataPoint[] = [];

    if (Array.isArray(result.charge_profile)) {
      result.charge_profile.forEach((chargeItem: any) => {
        if (typeof chargeItem === 'object' && chargeItem !== null && 'ph' in chargeItem && 'charge' in chargeItem) {
          chargeData.push({
            ph: Number(chargeItem.ph),
            charge: Number(chargeItem.charge),
            sequence_length: result.sequence_length
          });
        }
      });
    }

    if (chargeData.length === 0) {
      return (
        <BaseResultDisplay
          title={title || 'pH电荷曲线'}
          data={data}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          showRawData={true}
          showDistribution={false}
        >
          <Alert
            message="无有效数据"
            description="没有找到有效的pH电荷曲线分析结果。"
            type="warning"
            showIcon
          />
        </BaseResultDisplay>
      );
    }

    chargeData.sort((a, b) => a.ph - b.ph);
    const chartData = chargeData.map(point => [point.ph, point.charge]);

    const findIsoelectricPoint = () => {
      for (let i = 0; i < chargeData.length - 1; i++) {
        if ((chargeData[i].charge >= 0 && chargeData[i+1].charge <= 0) ||
            (chargeData[i].charge <= 0 && chargeData[i+1].charge >= 0)) {
          const ph1 = chargeData[i].ph;
          const ph2 = chargeData[i+1].ph;
          const charge1 = chargeData[i].charge;
          const charge2 = chargeData[i+1].charge;
          if (charge1 === charge2) return ph1;
          const isoelectricPoint = ph1 + (0 - charge1) * (ph2 - ph1) / (charge2 - charge1);
          return isoelectricPoint.toFixed(2);
        }
      }
      return "未找到";
    };

    const maxCharge = Math.max(...chargeData.map(point => point.charge));
    const minCharge = Math.min(...chargeData.map(point => point.charge));
    const sequenceLength = chargeData[0]?.sequence_length || '未知';

    const option = {
      tooltip: {
        trigger: 'axis',
        formatter: function(params: any) {
          const dataPoint = params[0];
          return `pH: ${dataPoint.data[0].toFixed(2)}<br/>电荷: ${dataPoint.data[1].toFixed(2)}`;
        }
      },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '10%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: 'pH值',
        nameLocation: 'middle',
        nameGap: 30,
        min: Math.floor(Math.min(...chargeData.map(point => point.ph))),
        max: Math.ceil(Math.max(...chargeData.map(point => point.ph)))
      },
      yAxis: {
        type: 'value',
        name: '电荷',
        nameLocation: 'middle',
        nameGap: 40
      },
      series: [
        {
          type: 'line',
          data: chartData,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#52c41a'
          },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#52c41a'
          }
        }
      ]
    };

    return (
      <BaseResultDisplay
        title={title || 'pH电荷曲线'}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
      >
        <Card
          title={`序列 ${singleEntry.sequence_id || singleEntry.id || '1'} - pH电荷曲线`}
          style={{ marginBottom: 16 }}
        >
          <ReactECharts
            option={option}
            style={{ height: '400px', width: '100%' }}
            notMerge={true}
          />
          <Card title="电荷分析" bordered={false} style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>等电点 (pI)</div>
                  <div style={styles.analysisValue}>{findIsoelectricPoint()}</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>最大电荷</div>
                  <div style={styles.analysisValue}>{maxCharge.toFixed(2)}</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>最小电荷</div>
                  <div style={styles.analysisValue}>{minCharge.toFixed(2)}</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Card>
      </BaseResultDisplay>
    );
  }

  if (Array.isArray(data) && data.length > 0) {
    return <ChargeProfileResult data={{ results: data }} title={title} onDeleteResult={onDeleteResult} multiResults={multiResults} />;
  }

  // 单序列模式的原有逻辑保持不变
  const [selectedPoint, setSelectedPoint] = React.useState<ChargeDataPoint | null>(null);
  
  // 处理不同格式的数据
  const chargeData: ChargeDataPoint[] = [];
  
  if (Array.isArray(data)) {
    // 如果已经是数组格式
    data.forEach(item => {
      if (typeof item === 'object' && item !== null && 'ph' in item && 'charge' in item) {
        chargeData.push({
          ph: Number(item.ph),
          charge: Number(item.charge),
          sequence_length: item.sequence_length
        });
      }
    });
  } else if (data && typeof data === 'object') {
    // 如果是表格格式数据
    if (Array.isArray(data.charge) && Array.isArray(data.ph)) {
      data.charge.forEach((charge: number, index: number) => {
        if (index < data.ph.length) {
          chargeData.push({
            charge: Number(charge),
            ph: Number(data.ph[index]),
            sequence_length: data.sequence_length
          });
        }
      });
    } else if (data.data && Array.isArray(data.data)) {
      // 嵌套格式
      data.data.forEach((item: any) => {
        if (item && typeof item === 'object' && 'ph' in item && 'charge' in item) {
          chargeData.push({
            ph: Number(item.ph),
            charge: Number(item.charge),
            sequence_length: item.sequence_length
          });
        }
      });
    }
  }
  
  // 如果没有有效数据，显示错误信息
  if (chargeData.length === 0) {
    console.error('无法解析pH电荷曲线数据:', data);
    return (
      <BaseResultDisplay
        title={title || "pH电荷曲线"}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={true}
        showDistribution={false}
      >
        <Alert 
          message="数据格式错误" 
          description="无法解析pH电荷曲线数据，请检查数据格式。" 
          type="error" 
          showIcon 
        />
      </BaseResultDisplay>
    );
  }
  
  // 排序数据点（按pH值从小到大）
  chargeData.sort((a, b) => a.ph - b.ph);
  
  // 准备图表数据
  const chartData = chargeData.map(item => [item.ph, item.charge]);
  
  // 图表配置
  const option = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        const dataPoint = params[0];
        return `pH: ${dataPoint.data[0].toFixed(2)}<br/>电荷: ${dataPoint.data[1].toFixed(2)}`;
      }
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '10%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      name: 'pH值',
      nameLocation: 'middle',
      nameGap: 30,
      min: Math.floor(Math.min(...chargeData.map(item => item.ph))),
      max: Math.ceil(Math.max(...chargeData.map(item => item.ph)))
    },
    yAxis: {
      type: 'value',
      name: '电荷',
      nameLocation: 'middle',
      nameGap: 40
    },
    series: [
      {
        type: 'line',
        data: chartData,
        smooth: true,
        lineStyle: {
          width: 2,
          color: '#52c41a'
        },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: '#52c41a'
        }
      }
    ]
  };
  
  // 处理图表点击事件
  const onChartClick = (params: any) => {
    const ph = params.data[0];
    const charge = params.data[1];
    const sequenceLength = chargeData.find(item => item.ph === ph)?.sequence_length;
    setSelectedPoint({ ph, charge, sequence_length: sequenceLength });
  };
  
  // 处理图表事件
  const onEvents = {
    'click': onChartClick
  };
  
  // 查找等电点（电荷为0的点）
  const findIsoelectricPoint = () => {
    // 寻找电荷从正变为负（或从负变为正）的点
    for (let i = 0; i < chargeData.length - 1; i++) {
      if ((chargeData[i].charge >= 0 && chargeData[i+1].charge <= 0) ||
          (chargeData[i].charge <= 0 && chargeData[i+1].charge >= 0)) {
        // 线性插值计算等电点
        const ph1 = chargeData[i].ph;
        const ph2 = chargeData[i+1].ph;
        const charge1 = chargeData[i].charge;
        const charge2 = chargeData[i+1].charge;
        
        // 避免除以零
        if (charge1 === charge2) return ph1;
        
        // 线性插值
        const isoelectricPoint = ph1 + (0 - charge1) * (ph2 - ph1) / (charge2 - charge1);
        return isoelectricPoint.toFixed(2);
      }
    }
    
    return "未找到";
  };
  
  // 找到最大和最小电荷值
  const maxCharge = Math.max(...chargeData.map(item => item.charge));
  const minCharge = Math.min(...chargeData.map(item => item.charge));
  const maxPh = Math.max(...chargeData.map(item => item.ph));
  const minPh = Math.min(...chargeData.map(item => item.ph));
  const sequenceLength = chargeData[0]?.sequence_length || '未知';
  
  return (
    <BaseResultDisplay
      title={title || 'pH电荷曲线'}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="pH电荷曲线" bordered={false}>
            <ReactECharts 
              option={option} 
              style={{ height: '400px', width: '100%' }}
              onEvents={onEvents}
            />
            
            {selectedPoint && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Tooltip title="当前选中的点">
                  <span style={{ 
                    padding: '8px 16px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    pH: <b>{selectedPoint.ph.toFixed(2)}</b>, 
                    电荷: <b>{selectedPoint.charge.toFixed(2)}</b>
                  </span>
                </Tooltip>
              </div>
            )}
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="电荷分析" bordered={false}>
            <Row gutter={16}>
              <Col span={8}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>等电点 (pI)</div>
                  <div style={styles.analysisValue}>{findIsoelectricPoint()}</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>最大电荷</div>
                  <div style={styles.analysisValue}>{maxCharge.toFixed(2)}</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>最小电荷</div>
                  <div style={styles.analysisValue}>{minCharge.toFixed(2)}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>序列长度</div>
                  <div style={styles.analysisValue}>{sequenceLength}</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </BaseResultDisplay>
  );
};

export default ChargeProfileResult;